// src/components/CartDropdown.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { Link, useLocation } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { useCart } from "../CartContext";
import { showSuccess, showError } from "toast/toast";
import imageCustomSquare from "assets/images/imagecustomrounded.png";

import { getAuthCookie } from "../../utils/authCookies";

type Base = "agent" | "realtor" | "teammember";

type Props = { base: Base; isVisible?: boolean };

const HEX24 = /^[a-f0-9]{24}$/i;
const ABS_URL = /^https?:\/\//i;
const isHexId = (s?: string) => !!(s && HEX24.test(s.trim()));
const asTrimmed = (v: unknown) => String(v ?? "").trim();
const CartDropdown: React.FC<Props> = ({ base, isVisible = true }) => {
  const { cartItems, fetchCart } = useCart();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchCart(); }, [location.pathname]); // refresh on route change
  const token = getAuthCookie("sessionToken") || "";
  // close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const total = cartItems.reduce((sum, it) => {
    const qty = Number(it.quantity || 0);
    const unitPrice = Number(
      (it as any).changed_unit_price ?? it.unit_retail_price ?? 0
    );
    return sum + qty * unitPrice;
  }, 0);

  // collect image ids used by cart list (product image + customized preview image)
  const allImageIds = useMemo(() => {
    const ids: string[] = [];
    for (const it of (cartItems || [])) {
      const isCustomized = Boolean(asTrimmed((it as any)?.design_id) && asTrimmed((it as any)?.design_name));
      const previewId = asTrimmed((it as any)?.preview_url);
      const productImageId = asTrimmed((it?.images?.[0]?.url as string) || (it?.image_url as string) || "");
      const preferred = isCustomized && previewId ? previewId : productImageId;
      if (isHexId(preferred)) ids.push(preferred.trim());
    }
    return Array.from(new Set(ids));
  }, [cartItems]);

  // EXACT shape your reference uses
  const dataForMap = useMemo(() => {
    if (allImageIds.length === 0) return [];
    return [
      {
        product: {
          variants: [
            { images: allImageIds.map((url) => ({ url })) },
          ],
        },
      },
    ];
  }, [allImageIds]);
  const imageUrlMap = useVariantImageUrlMap(dataForMap, token);
  const handleRemove = async (productId: string, variantId: string) => {
    try {
      const token = getAuthCookie("sessionToken");
      const cartId = localStorage.getItem("cart_id");
      if (!token || !cartId) throw new Error("Missing token/cartId");
      
      // Check if this is the last item BEFORE deleting
      const isLastItem = cartItems.length === 1;
      
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/carts/${cartId}/products/${productId}/variants/${variantId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (!res.ok) throw new Error("Delete failed");

      // If this was the last item, delete the entire cart
      if (isLastItem) {
        try {
          const deleteCartRes = await fetch(
            `${process.env.REACT_APP_API_URL}/carts/${cartId}`,
            { method: "DELETE", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
          );
          if (deleteCartRes.ok) {
            localStorage.removeItem("cart_id");
            showSuccess("Cart cleared");
          }
        } catch (error) {
          console.error("Failed to delete cart:", error);
        }
      }
      
      await fetchCart();
      showSuccess("Removed from cart.");
    } catch {
      showError("Failed to remove item.");
    }
  };

  if (!isVisible) return null;

  const shopNowHref =
    base === "agent" ? "/agent/suppliers"
      : base === "realtor" ? "/realtor/suppliers"
        : "/teammember/suppliers";

  const viewCartHref =
    base === "agent" ? "/agent/order/cart"
      : base === "realtor" ? "/realtor/order/cart"
        : "/teammember/order/cart";

  const checkoutHref =
    base === "agent" ? "/agent/order/checkout"
      : base === "realtor" ? "/realtor/order/checkout"
        : "/teammember/order/checkout";

  return (
    <div ref={dropdownRef} className="topbar-head-dropdown ms-1 header-item">
      {/* tiny, scoped CSS for scrollbar thinning */}
      <style>{`
        .cart-scroll { scrollbar-width: thin; }                /* Firefox */
        .cart-scroll::-webkit-scrollbar { width: 6px; }        /* WebKit */
        .cart-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.2); border-radius: 8px; }
      `}</style>

      <Dropdown show={isOpen} onToggle={(open) => setIsOpen(open)}>
        {/* Trigger */}
        <Dropdown.Toggle
          as="button"
          className="cart-header-icon cart-dropdown-trigger border-0 bg-transparent p-0 d-inline-flex align-items-center justify-content-center"
          style={{ outline: "none", minWidth: 32, minHeight: 32 }}
          onClick={() => { fetchCart(); setIsOpen(!isOpen); }}
          aria-label="Open cart"
        >
          <span className="cart-header-icon-wrap">
            🛒
            <span className="cart-badge" id="cartBadgeCount">{cartItems.length}</span>
          </span>
        </Dropdown.Toggle>

        {/* Menu */}
        <Dropdown.Menu
          align="end"
          className="p-0 shadow-lg br-8 overflow-hidden border-3"
          style={{ width: 360, maxWidth: "90vw" }}
        >
          {/* Header: left title+count, right close */}
          <div className="bg-white border-bottom px-3 py-2">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-semibold">My Cart</h6>
                <span className="">
                  ({cartItems.length}) items
                </span>
              </div>
              <button
                type="button"
                className="btn btn-ghost-dark p-1 text-secondary"
                onClick={() => setIsOpen(false)}
                aria-label="Close cart"
                title="Close"
              >
                <i className="ri-close-line fs-5" />
              </button>
            </div>
          </div>

          {/* Items (scroll container) */}
          <div className="bg-white cart-scroll" style={{ maxHeight: 260, overflowY: "auto" }}>
            {cartItems.length ? (
              <ul className="list-group list-group-flush">
                {cartItems.map((item, idx) => {
                  const unitPrice = Number(
                    (item as any).changed_unit_price ?? item.unit_retail_price ?? 0
                  );
                  const price = unitPrice * Number(item.quantity || 0);
                  const isCustomized = Boolean(asTrimmed((item as any)?.design_id) && asTrimmed((item as any)?.design_name));
                  const previewRef = asTrimmed((item as any)?.preview_url);
                  const productRef = asTrimmed((item?.images?.[0]?.url as string) || (item?.image_url as string) || "");
                  const raw = isCustomized && previewRef ? previewRef : productRef;
                  let imgSrc: string = "";
                  if (raw && ABS_URL.test(raw)) {
                    imgSrc = raw;
                  } else {

                    const id = isHexId(raw) ? raw.trim() : "";
                    const map: Record<string, string> = (imageUrlMap as Record<string, string>) || {};
                    imgSrc = id ? (map[id] ?? getImageSrc(id, map) ?? "") : "";
                  }
                  if (!imgSrc) imgSrc = imageCustomSquare;
                  return (
                    <li key={idx} className="list-group-item px-3 py-3 d-flex align-items-center">
                      {/* thumb */}
                      <img
                        src={imgSrc}
                        alt={item.name || "Product"}
                        className="flex-shrink-0 me-3"
                        style={{ width: 50, height: 62, objectFit: "contain" }}
                        onError={(e) => {
                          e.currentTarget.src = imageCustomSquare;
                        }}
                      />
                      {/* fixed name block with ellipsis */}
                      <div className="flex-shrink-0" style={{ width: 180 }}>
                        <div className="fw-bold text-truncate" title={item.name || "Product"}>
                          {item.name}
                        </div>
                        <div className="text-muted small">Qty: {item.quantity}</div>
                      </div>
                      {/* price column: fixed width, right aligned */}
                      <div className="text-end fw-semibold ms-auto flex-shrink-0" style={{ width: 84 }}>
                        ${price.toFixed(2)}
                      </div>
                      {/* remove column: fixed width so all X's line up */}
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-secondary p-0 ms-2 flex-shrink-0"
                        style={{ width: 28 }}
                        title="Remove"
                        aria-label="Remove"
                        onClick={() => handleRemove(item.product_id, item.variant_id)}
                      >
                        <i className="ri-close-line fs-5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-center py-4 px-3">
                <h6 className="mb-2">Your cart is empty</h6>
                <Link to={shopNowHref} className="btn btn-outline-primary btn-sm" onClick={() => setIsOpen(false)}>
                  Shop Now
                </Link>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-white border-top px-3 py-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fs-17 fw-bold">Total:</span>
              <span className="fs-17 fw-bold">${total.toFixed(2)}</span>
            </div>
            <div className="d-flex gap-2">
              <Link to={viewCartHref} className="btn back-btn surface-btn fw-bold w-50" onClick={() => setIsOpen(false)}>
                View Cart
              </Link>
              <Link to={checkoutHref} className="btn btn-primary table-addbutton w-50" onClick={() => setIsOpen(false)}>
                Checkout
              </Link>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default CartDropdown;
